// src/pages/products/components/DropshippingVariationsSection.jsx
import React, { useState } from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './DropshippingVariationsSection.module.css';

/**
 * Dropshipping tab content for variable products.
 * Shows each variation as a table row with per-variation dropshipping fields,
 * plus a bulk-edit bar at the top.
 *
 * Props:
 *   variations                   array of variation objects from formData
 *   onVariationDropshippingChange(index, field, value)  handler
 *   onBulkApply(bulkData)         handler
 */
const DropshippingVariationsSection = ({ variations, onVariationDropshippingChange, onBulkApply }) => {
  const { t } = useLocalization();

  const [bulk, setBulk] = useState({
    dropshippingEnabled: false,
    dropshippingPrice: '',
    minimumSellingPrice: '',
    maximumSellingPrice: '',
  });

  const getVariationLabel = (variation) => {
    if (variation.attributeValues && variation.attributeValues.length > 0) {
      return variation.attributeValues.map((av) => av.value).join(' / ');
    }
    return variation.sku || `Variation ${variation.id || ''}`;
  };

  const handleBulkApply = () => {
    onBulkApply(bulk);
  };

  if (!variations || variations.length === 0) {
    return (
      <div className={styles.section}>
        <p className={styles.emptyMsg}>{t('products.noVariationsAvailable')}</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      {/* Bulk Edit Bar */}
      <div className={styles.bulkBar}>
        <h4 className={styles.bulkTitle}>{t('dropshipping.bulkEdit')}</h4>
        <div className={styles.bulkFields}>
          <label className={styles.bulkToggleLabel}>
            <input
              type="checkbox"
              checked={bulk.dropshippingEnabled}
              onChange={(e) => setBulk((prev) => ({ ...prev, dropshippingEnabled: e.target.checked }))}
            />
            <span>{t('dropshipping.enableProvider')}</span>
          </label>
          <div className={styles.bulkInputGroup}>
            <label className={styles.bulkLabel}>{t('dropshipping.dropshippingPrice')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.bulkInput}
              value={bulk.dropshippingPrice}
              placeholder="0.00"
              onChange={(e) => setBulk((prev) => ({ ...prev, dropshippingPrice: e.target.value }))}
            />
          </div>
          <div className={styles.bulkInputGroup}>
            <label className={styles.bulkLabel}>{t('dropshipping.minSellingPrice')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.bulkInput}
              value={bulk.minimumSellingPrice}
              placeholder="0.00"
              onChange={(e) => setBulk((prev) => ({ ...prev, minimumSellingPrice: e.target.value }))}
            />
          </div>
          <div className={styles.bulkInputGroup}>
            <label className={styles.bulkLabel}>{t('dropshipping.maxSellingPrice')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.bulkInput}
              value={bulk.maximumSellingPrice}
              placeholder="0.00"
              onChange={(e) => setBulk((prev) => ({ ...prev, maximumSellingPrice: e.target.value }))}
            />
          </div>
          <button type="button" className={styles.applyBtn} onClick={handleBulkApply}>
            {t('common.applyToAll')}
          </button>
        </div>
      </div>

      {/* Variations Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>{t('products.variation')}</th>
              <th className={styles.th}>{t('dropshipping.enable')}</th>
              <th className={styles.th}>{t('dropshipping.dropshippingPrice')}</th>
              <th className={styles.th}>{t('dropshipping.minSellingPrice')}</th>
              <th className={styles.th}>{t('dropshipping.maxSellingPrice')}</th>
            </tr>
          </thead>
          <tbody>
            {variations.map((variation, index) => {
              const isEnabled = !!variation.dropshippingEnabled;
              return (
                <tr key={variation.id || index} className={styles.tr}>
                  <td className={styles.td}>
                    <span className={styles.variationLabel}>{getVariationLabel(variation)}</span>
                    {variation.sku && (
                      <span className={styles.variationSku}>{variation.sku}</span>
                    )}
                  </td>
                  <td className={styles.tdCenter}>
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) =>
                          onVariationDropshippingChange(index, 'dropshippingEnabled', e.target.checked)
                        }
                      />
                      <span className={styles.toggleSlider} />
                    </label>
                  </td>
                  <td className={styles.td}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.input}
                      value={variation.dropshippingPrice || ''}
                      placeholder="0.00"
                      onChange={(e) =>
                        onVariationDropshippingChange(index, 'dropshippingPrice', e.target.value)
                      }
                    />
                    {isEnabled && !variation.dropshippingPrice && (
                      <span className={styles.requiredHint}>{t('common.required')}</span>
                    )}
                  </td>
                  <td className={styles.td}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.input}
                      value={variation.minimumSellingPrice || ''}
                      placeholder="0.00"
                      onChange={(e) =>
                        onVariationDropshippingChange(index, 'minimumSellingPrice', e.target.value)
                      }
                    />
                    {isEnabled && !variation.minimumSellingPrice && (
                      <span className={styles.requiredHint}>{t('common.required')}</span>
                    )}
                  </td>
                  <td className={styles.td}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.input}
                      value={variation.maximumSellingPrice || ''}
                      placeholder="0.00"
                      onChange={(e) =>
                        onVariationDropshippingChange(index, 'maximumSellingPrice', e.target.value)
                      }
                    />
                    {isEnabled && !variation.maximumSellingPrice && (
                      <span className={styles.requiredHint}>{t('common.required')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DropshippingVariationsSection;
